import { isNumber } from 'lodash';
import {
    AssessmentType,
    ExternalAssessmentStates,
    ScoringMethodType
} from '../constants-types/Enums';
import { DateFormatUtility } from '../core-utilities/DateFormat.utility';
import { Util } from '../core-utilities/Util';
import { ITranslationService } from '../system-interface/ITranslationService.interface';
import { Assessment } from './Assessment.model';
import { AssessmentMethods } from './AssessmentMethods';
import { AssessmentReminderConfig } from './AssessmentReminderConfig';
import { FullPattern } from './Pattern.model';

export type CustomField = {
    fieldName: string;
    fieldValue: string;
};

export class BehavioralAssessment implements Assessment {
    readonly expirationDate?: Date;
    readonly patternNumber: number;
    readonly scoringMethod?: ScoringMethodType;
    readonly type?: AssessmentType = AssessmentType.Behavioral;
    readonly assessmentConsent: string;
    readonly scoreId: string;

    readonly customFields: CustomField[] = [];

    readonly administeredBy: string;
    readonly administeredById: string;
    readonly completedDate: Date;
    readonly sentDate: Date;
    readonly id: string;
    readonly assessmentState?: ExternalAssessmentStates;
    readonly pattern?: FullPattern; // its self property is same as selfSigmaScore in backend
    /* reminderSentDate is not a readonly property so that the date can be modified each time the user is sent a reminder */
    reminderSentDate: Date;

    // metadata info
    // scoreId: string;

    constructor(util: Util, obj?: Partial<BehavioralAssessment>) {
        Object.assign(this, obj);
        if (obj) {
            // Typescript made it hard to do in a more elegant way
            if (obj.completedDate && !(obj.completedDate instanceof Date)) {
                this.completedDate = util.newDate(obj.completedDate);
            }
            if (obj.expirationDate && !(obj.expirationDate instanceof Date)) {
                this.expirationDate = util.newDate(obj.expirationDate);
            }
            if (obj.sentDate && !(obj.sentDate instanceof Date)) {
                this.sentDate = util.newDate(obj.sentDate);
            }
            if (obj.sentDate) {
                if (obj.sentDate instanceof Date) {
                    const reminderDate = new Date();
                    obj.reminderSentDate = reminderDate;
                    this.reminderSentDate = util.newDate(obj.reminderSentDate);
                }
            }
        }
    }

    scoringMethodString(TR: ITranslationService): string {
        switch (this.scoringMethod) {
            case ScoringMethodType.Checklist:
                return TR.key('label.common-terms.scoring-method.checklist');
            case ScoringMethodType.CombinedPattern:
                return TR.key(
                    'label.common-terms.scoring-method.combinedpattern'
                );
            case ScoringMethodType.Datasheet:
                return TR.key('label.common-terms.scoring-method.datasheet');
            case ScoringMethodType.EmailInvitation:
                return TR.key(
                    'label.common-terms.scoring-method.emailinvitation'
                );
            case ScoringMethodType.OpenInvitation:
                return TR.key('label.assessment.openinvitation.title');
            case ScoringMethodType.Unknown:
                return TR.key('common.unknown');
        }
        return '';
    }

    hasPattern(): boolean {
        // the check on A should be unneeded, but trying to be a little more
        // bulletproof
        return !!(
            this.pattern &&
            this.pattern.self &&
            isNumber(this.pattern.self.A)
        );
    }

    isPending(): boolean {
        return this.assessmentState === ExternalAssessmentStates.Pending;
    }

    isExpired(): boolean {
        if (this.assessmentState === ExternalAssessmentStates.Expired) {
            return true;
        }
        if (this.isPending()) {
            if (!this.expirationDate) {
                return false;
            }
            return new Date() > this.expirationDate;
        }
        return false;
    }

    isPendingOrExpired(): boolean {
        return this.isPending() || this.isExpired();
    }

    getReminderConfig(
        personId: string,
        lastSentDate: Date,
        TR: ITranslationService,
        dateFormat: DateFormatUtility,
        assessmentId?: string
    ): AssessmentReminderConfig {
        return AssessmentMethods.getReminderConfig(
            this,
            AssessmentType.Behavioral,
            personId,
            lastSentDate,
            TR,
            dateFormat,
            assessmentId
        );
    }

    getReminderDescription(date: DateFormatUtility, TR: ITranslationService) {
        return AssessmentMethods.getReminderDescription(this, date, TR);
    }
}
